import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import skuUtils from 'utils/Sku';
import helperUtils from 'utils/Helpers';
import Empty from 'constants/empty';

const { getProp } = helperUtils;

class OOSAlternateLinkBindings {
    /**
     * Track when user clicks "Select alternate" for an out of stock sample/reward/GWP
     * All OOS items should fire consistent pageName when clicking "Select alternate" on basket page
     * @param {object} item - The basket item
     */
    static trackSelectAlternateClick = item => {
        if (!item || !item.sku) {
            return;
        }

        // All OOS item types should have consistent pageName when clicking "Select alternate"
        const pageDetail = anaConsts.PAGE_TYPES.BASKET;
        const pageName = `basket:${anaConsts.PAGE_TYPES.BASKET}:n/a:*`;

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName,
                pageDetail,
                actionInfo: anaConsts.ACTION_INFO.SELECT_ALTERNATE,
                linkName: anaConsts.ACTION_INFO.SELECT_ALTERNATE,
                internalCampaign: pageName
            }
        });
    };

    /**
     * Track error message analytics for OOS items on basket page load
     * All OOS items (samples, rewards, GWP) should fire consistent pageName on basket page load
     * @param {object} details - Error details
     * @param {string} details.fieldName - Field name ('smart sample', 'bi-reward', or 'gwp-reward')
     * @param {string} details.errorMessage - Error message to track
     */
    static trackErrorMessageAnalytics = details => {
        if (!details) {
            return;
        }

        const { fieldName, errorMessage } = details;

        // All OOS item types should have consistent pageName on basket page load
        const pageDetail = anaConsts.PAGE_TYPES.BASKET;
        const pageName = `basket:${anaConsts.PAGE_TYPES.BASKET}:n/a:*`;

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName,
                pageDetail,
                fieldErrors: `sample oos: ${fieldName}`,
                errorMessages: errorMessage
            }
        });
    };

    /**
     * Track when the product samples modal opens for OOS item
     * Fires ASYNC_PAGE_LOAD event (s.t() call) when modal opens for "Select Alternate"
     * @param {object} item - The item object (sample, BI reward, or GWP)
     */
    static trackProductSamplesModalOpen = item => {
        if (!item) {
            return;
        }

        const isSample = skuUtils.isSample(item);
        const isBiReward = skuUtils.isBiReward(item);
        const isGwp = skuUtils.isGwp(item);

        let pageDetail, pageName;

        // Determine the correct pageName and pageDetail based on item type
        if (isSample) {
            pageDetail = anaConsts.PAGE_DETAIL.SAMPLES;
            pageName = `basket:${anaConsts.PAGE_DETAIL.SAMPLES}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        } else if (isBiReward) {
            pageDetail = anaConsts.PAGE_DETAIL.REWARDS_BAZAAR;
            pageName = `basket:${anaConsts.PAGE_DETAIL.REWARDS_BAZAAR}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        } else if (isGwp) {
            pageDetail = anaConsts.PAGE_DETAIL.FREE_GIFT_MODAL;
            pageName = `basket:${anaConsts.PAGE_DETAIL.FREE_GIFT_MODAL}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        }

        // Only fire analytics if we have a valid pageName
        if (pageName) {
            // Safe access to digitalData to avoid ReferenceError in SSR context
            let previousPageName = Empty.String;

            try {
                previousPageName = getProp(digitalData, 'page.attributes.sephoraPageInfo.pageName') || Empty.String;
            } catch (error) {
                // digitalData not available (SSR context or not initialized)
                previousPageName = Empty.String;
            }

            // Fire ASYNC_PAGE_LOAD event (s.t() call) for modal pageload tracking
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    pageName,
                    previousPageName,
                    pageDetail,
                    pageType: anaConsts.PAGE_TYPES.BASKET
                }
            });
        }
    };

    /**
     * Track when the Product Samples Modal is closed
     * @param {object} data - Analytics data
     * @param {string} data.productId - The main product ID
     * @param {number} data.samplesSelected - Number of samples selected
     * @param {boolean} data.hasOutOfStockAttempt - Whether user tried to select OOS samples
     */
    static trackProductSamplesModalClose = (data = {}) => {
        const { productId, samplesSelected, hasOutOfStockAttempt } = data;
        const pageType = anaConsts.PAGE_TYPES.PRODUCT;
        const pageDetail = 'product samples modal';
        const actionText = hasOutOfStockAttempt
            ? `close:${productId}:selected:${samplesSelected}:oos-attempt`
            : `close:${productId}:selected:${samplesSelected}`;
        const link = `${pageType}:${pageDetail}:${actionText}`;

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName: `${pageType}:${pageDetail}:${productId}:*`,
                pageType,
                pageDetail,
                linkName: link,
                internalCampaign: link
            }
        });
    };
}

export default OOSAlternateLinkBindings;
